import React, { useState, useContext } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { Button } from './ui/button';
import { ContentContext } from '../App';

const TestimonialsSection = () => {
  const { language } = useLanguage();
  const content = useContext(ContentContext);
  const [activeIndex, setActiveIndex] = useState(0);

  const testimonials = content?.testimonials || [];

  if (testimonials.length === 0) return null;

  const next = () => {
    setActiveIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prev = () => {
    setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const currentTestimonial = testimonials[activeIndex];

  return (
    <section id="testimonials" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-amber-600 font-semibold tracking-wide uppercase text-sm">
            {language === 'en' ? 'Testimonials' : '客户评价'}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2">
            {language === 'en' ? 'What Our Clients Say' : '客户评价'}
          </h2>
          <p className="text-slate-600 mt-4 text-lg">
            {language === 'en' ? 'Trusted by Homeowners Across GTA' : '深受大多伦多地区房主信赖'}
          </p>
        </div>

        {/* Featured Testimonial */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 relative">
            <Quote className="absolute top-8 left-8 w-12 h-12 text-amber-100" />
            
            <div className="relative">
              {/* Stars */}
              <div className="flex gap-1 mb-6">
                {[...Array(currentTestimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-6 h-6 fill-amber-400 text-amber-400" />
                ))}
              </div>

              {/* Quote */}
              <blockquote className="text-xl md:text-2xl text-slate-700 leading-relaxed mb-8">
                "{language === 'en' ? currentTestimonial.text_en : currentTestimonial.text_zh}"
              </blockquote>

              {/* Author */}
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-slate-200 rounded-full flex items-center justify-center">
                  <span className="text-xl font-bold text-slate-600">
                    {currentTestimonial.name?.charAt(0)}
                  </span>
                </div>
                <div>
                  <div className="font-semibold text-slate-900">
                    {currentTestimonial.name}
                  </div>
                  <div className="text-slate-500">
                    {language === 'en' ? currentTestimonial.location_en : currentTestimonial.location_zh}
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="absolute bottom-8 right-8 flex gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={prev}
                className="border-slate-200 hover:bg-slate-50"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={next}
                className="border-slate-200 hover:bg-slate-50"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Testimonial Indicators */}
        <div className="flex justify-center gap-2">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => setActiveIndex(index)}
              className={`w-3 h-3 rounded-full transition-colors ${
                index === activeIndex ? 'bg-amber-500' : 'bg-slate-300 hover:bg-slate-400'
              }`}
            />
          ))}
        </div>

        {/* Mini Cards */}
        <div className="grid md:grid-cols-4 gap-4 mt-12">
          {testimonials.map((testimonial, index) => (
            <button
              key={testimonial.id}
              onClick={() => setActiveIndex(index)}
              className={`p-4 rounded-xl text-left transition-all ${
                index === activeIndex
                  ? 'bg-amber-500 text-slate-900'
                  : 'bg-white hover:bg-slate-100 text-slate-600'
              }`}
            >
              <div className="flex gap-1 mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-4 h-4 ${
                      index === activeIndex
                        ? 'fill-slate-900 text-slate-900'
                        : 'fill-amber-400 text-amber-400'
                    }`}
                  />
                ))}
              </div>
              <div className={`font-semibold ${
                index === activeIndex ? 'text-slate-900' : 'text-slate-800'
              }`}>
                {testimonial.name}
              </div>
              <div className={`text-sm ${
                index === activeIndex ? 'text-slate-800' : 'text-slate-500'
              }`}>
                {language === 'en' ? testimonial.location_en : testimonial.location_zh}
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
